import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "../../styles/AssessmentDetails.css";
import { signedAuthHeaders } from "../../api";

const AssessmentDetails = () => {
    const { assessmentId } = useParams();
    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Take-exam state (REACH-1344): questions come from the server without the
    // answer key; answers are scored server-side.
    const [questions, setQuestions] = useState(null);
    const [selected, setSelected] = useState({}); // questionId -> answerId
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState("");

    useEffect(() => {
        fetch(`/api/assessmentdetails/${assessmentId}`)
            .then(response => response.json())
            .then(data => {
                setAssessment(data);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, [assessmentId]);

    const handleBuyAssessment = async () => {
        try {
            const response = await fetch(`/api/assessment-purchases/buy/${assessmentId}`, {
                method: "POST",
                headers: await signedAuthHeaders(),
            });
            setNotice(await response.text());
        } catch (e) {
            setNotice(e?.message || "Purchase failed.");
        }
    };

    const startExam = async () => {
        setBusy(true);
        setNotice("");
        setResult(null);
        try {
            const res = await fetch(`/api/assessment-purchases/take/${assessmentId}`, {
                headers: await signedAuthHeaders(),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.error || "Could not load the exam. Buy it first.");
            }
            setQuestions(Array.isArray(data) ? data : []);
        } catch (e) {
            setNotice(e?.message || "Could not start the exam.");
        } finally {
            setBusy(false);
        }
    };

    const submitExam = async () => {
        setBusy(true);
        setNotice("");
        try {
            const answers = Object.entries(selected).map(([questionId, answerId]) => ({
                questionId: Number(questionId),
                answerId: Number(answerId),
            }));
            if (!answers.length) {
                throw new Error("Answer at least one question.");
            }
            const headers = { ...(await signedAuthHeaders()), "Content-Type": "application/json" };
            const res = await fetch(`/api/assessment-purchases/attempt/${assessmentId}`, {
                method: "POST",
                headers,
                body: JSON.stringify({ answers }),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.error || "Submission failed.");
            }
            setResult(data); // { score, status, attempts } — scored server-side
        } catch (e) {
            setNotice(e?.message || "Submission failed.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;
    if (!assessment) return <p>No assessment found.</p>;

    return (
        <div className="assessment-details">
            <h1>{assessment.title}</h1>
            <p>{assessment.description}</p>
            <p><strong>Price:</strong> {assessment.price} WSD</p>
            <p><strong>Total Questions:</strong> {assessment.totalQuestions}</p>

            {notice && <p className="notice">{notice}</p>}

            {!questions && (
                <div className="assessment-actions">
                    <button onClick={handleBuyAssessment} disabled={busy}>Buy Assessment</button>
                    <button onClick={startExam} disabled={busy}>Take Assessment</button>
                </div>
            )}

            {questions && !result && (
                <form
                    className="assessment-exam"
                    onSubmit={(e) => { e.preventDefault(); submitExam(); }}
                >
                    {questions.map((q, i) => (
                        <fieldset key={q.questionId} className="exam-question">
                            <legend>{i + 1}. {q.text}</legend>
                            {(q.answers || []).map((a) => (
                                <label key={a.answerId} className="exam-option">
                                    <input
                                        type="radio"
                                        name={`q_${q.questionId}`}
                                        value={a.answerId}
                                        checked={String(selected[q.questionId]) === String(a.answerId)}
                                        onChange={() =>
                                            setSelected((prev) => ({ ...prev, [q.questionId]: a.answerId }))
                                        }
                                    />
                                    {a.text}
                                </label>
                            ))}
                        </fieldset>
                    ))}
                    <button type="submit" disabled={busy}>Submit answers</button>
                </form>
            )}

            {result && (
                <div className="assessment-result">
                    <h2>Your result</h2>
                    <p><strong>Score:</strong> {String(result.score)}%</p>
                    <p><strong>Status:</strong> {result.status}</p>
                    <p><strong>Attempts:</strong> {result.attempts}</p>
                </div>
            )}
        </div>
    );
};

export default AssessmentDetails;
